import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowRight, Shield, Users, Calendar, Clock, ChevronRight, HelpCircle } from 'lucide-react';

/**
 * INDIVIDUALS LANDING PAGE
 * 
 * Entry point for adults (18+) discovering LikeKind.
 * 
 * Structure:
 * 1. What it is
 * 2. Who it's for
 * 3. How it works (3 bullets)
 * 4. Safety promise
 * 5. Primary CTA
 * 6. Secondary CTA to FAQ
 */
export default function IndividualsLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-sm" style={{ backgroundColor: 'rgba(244, 239, 230, 0.95)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" 
                alt="LikeKind" 
                className="w-8 h-8 rounded-lg"
              />
              <span className="font-bold" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>LikeKind</span>
            </button>
            <Button 
              onClick={() => navigate('/auth')}
              size="sm"
              className="rounded-full"
              style={{ backgroundColor: 'var(--lk-primary-green)', color: 'var(--lk-warm-sand)' }}
              data-testid="individuals-enter-btn"
            >
              Enter
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-12">
        
        {/* What It Is */}
        <section className="mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
            A place to be present.
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
            LikeKind is a calm space for people who want to connect with others — through groups, 
            activities, and shared interests — without the noise of traditional social platforms.
          </p>
        </section>

        {/* Who It's For */}
        <section className="mb-12 p-6 rounded-2xl" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--lk-primary-green)' }}>
              <Users className="w-5 h-5" style={{ color: 'var(--lk-warm-sand)' }} />
            </div>
            <h2 className="text-xl font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Who it&apos;s for</h2>
          </div>
          <p style={{ color: 'var(--fy-text-secondary)' }}>
            Adults (18+) who want to participate in local groups, join Commons, 
            explore timelines with SCUTA, and connect with people nearby — on their own terms.
          </p>
        </section>

        {/* How It Works */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold mb-6" style={{ color: 'var(--fy-text-primary)' }}>How it works</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage-muted)' }}>
                <span className="text-sm font-bold" style={{ color: 'var(--lk-primary-green)' }}>1</span>
              </div>
              <div>
                <h3 className="font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>Create your space</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  Set up Base Camp — your private dashboard. Choose what&apos;s visible and to whom.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage-muted)' }}>
                <span className="text-sm font-bold" style={{ color: 'var(--lk-primary-green)' }}>2</span>
              </div>
              <div>
                <h3 className="font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>Join or create groups</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  Find local activities. Create groups for things you want to do. Connect with people nearby.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage-muted)' }}>
                <span className="text-sm font-bold" style={{ color: 'var(--lk-primary-green)' }}>3</span>
              </div>
              <div>
                <h3 className="font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>Participate when it makes sense</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  No pressure to post. No engagement metrics. Just real moments with real people.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Safety Promise */}
        <section className="mb-12 p-6 rounded-2xl" style={{ backgroundColor: 'var(--lk-sage-muted)', border: '1px solid var(--lk-primary-green)' }}>
          <div className="flex items-center gap-3 mb-3">
            <Shield className="w-5 h-5" style={{ color: 'var(--lk-primary-green)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--lk-primary-green)' }}>Safety promise</h2>
          </div>
          <p className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>
            Privacy is the foundation. No algorithms. No engagement tricks. No selling your data. 
            You control what you share and with whom.
          </p>
        </section>

        {/* CTAs */}
        <section className="space-y-4">
          <Button 
            onClick={() => navigate('/auth')}
            className="w-full h-14 rounded-xl text-lg font-semibold"
            style={{ backgroundColor: 'var(--lk-primary-green)', color: 'var(--lk-warm-sand)' }}
            data-testid="individuals-create-account-btn"
          >
            Create Account
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <Button 
            onClick={() => navigate('/faq/individuals')}
            variant="outline"
            className="w-full h-12 rounded-xl font-medium"
            style={{ borderColor: 'var(--fy-border-subtle)', color: 'var(--fy-text-secondary)' }}
            data-testid="individuals-faq-btn"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            How does this work? (FAQ)
          </Button>
        </section>

        {/* Footer Links */}
        <footer className="mt-16 pt-8 flex flex-wrap gap-4 justify-center text-sm" style={{ borderTop: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-disabled)' }}>
          <button onClick={() => navigate('/for/families')} className="hover:underline">For Families</button>
          <span>·</span>
          <button onClick={() => navigate('/for/schools')} className="hover:underline">For Schools</button>
          <span>·</span>
          <button onClick={() => navigate('/for/institutions')} className="hover:underline">For Institutions</button>
          <span>·</span>
          <button onClick={() => navigate('/privacy')} className="hover:underline">Privacy</button>
          <span>·</span>
          <button onClick={() => navigate('/terms')} className="hover:underline">Terms</button>
        </footer>
      </main>
    </div>
  );
}
